import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { searchApi } from "../../services/api/search.api";
import { CourseOption } from "../../types/course.response";
import { getCourseKey } from "../../utils/courseUtils";
import Loading from "../loading/Loading";
import SearchIcon from "../icons/SearchIcon";

interface MissingCourseModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (course: CourseOption) => void | Promise<void>;
}

export default function MissingCourseModal({
    isOpen,
    onClose,
    onConfirm,
}: MissingCourseModalProps) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<CourseOption[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCourseKey, setSelectedCourseKey] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const requestIdRef = useRef(0);

    const selectedCourse = useMemo(
        () => results.find((course) => getCourseKey(course) === selectedCourseKey) ?? null,
        [results, selectedCourseKey]
    );

    const resetState = useCallback(() => {
        setQuery("");
        setResults([]);
        setError(null);
        setIsLoading(false);
        setIsSubmitting(false);
        setSelectedCourseKey(null);
        requestIdRef.current += 1;
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            resetState();
            return;
        }

        setTimeout(() => inputRef.current?.focus(), 0);
    }, [isOpen, resetState]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isSubmitting) {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, isSubmitting, onClose]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    const runSearch = useCallback(async (searchQuery: string) => {
        const trimmedQuery = searchQuery.trim();
        if (trimmedQuery.length < 2) {
            setResults([]);
            setError(null);
            setIsLoading(false);
            setSelectedCourseKey(null);
            return;
        }

        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);

        try {
            const searchResults = await searchApi.searchCourses(trimmedQuery);
            if (requestId !== requestIdRef.current) return;
            setResults(searchResults);
            setSelectedCourseKey((currentSelection) => {
                if (currentSelection && searchResults.some((course) => getCourseKey(course) === currentSelection)) {
                    return currentSelection;
                }
                return searchResults[0] ? getCourseKey(searchResults[0]) : null;
            });
        } catch (searchError) {
            if (requestId !== requestIdRef.current) return;
            console.error("Missing course search error:", searchError);
            setResults([]);
            setSelectedCourseKey(null);
            setError("Failed to search courses");
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, []);

    const handleChangeQuery = (nextQuery: string) => {
        setQuery(nextQuery);
        setSelectedCourseKey(null);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            runSearch(nextQuery);
        }, 200);
    };

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!selectedCourse || isSubmitting) return;

        setIsSubmitting(true);
        try {
            await onConfirm(selectedCourse);
            onClose();
        } catch {
            // Error is handled and displayed by the parent page
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4" onClick={handleClose}>
            <div
                className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Add other courses</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Search any course and add it to this requirement slot.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Close"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="px-6 py-5">
                    <div className="relative">
                        <SearchIcon
                            width={16}
                            height={16}
                            strokeColor="#9ca3af"
                            strokeWidth={3}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                        />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(event) => handleChangeQuery(event.target.value)}
                            placeholder="Search by subject, number, or title"
                            className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-10 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        {isLoading && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <Loading size="sm" />
                            </div>
                        )}
                    </div>

                    <div className="mt-4 rounded-lg border border-gray-200">
                        <div className="max-h-80 overflow-y-auto">
                            {error ? (
                                <div className="px-4 py-6 text-sm text-red-600">{error}</div>
                            ) : query.trim().length < 2 ? (
                                <div className="px-4 py-6 text-sm text-gray-500">
                                    Enter at least 3 characters to search for a course.
                                </div>
                            ) : !isLoading && results.length === 0 ? (
                                <div className="px-4 py-6 text-sm text-gray-500">No courses found.</div>
                            ) : (
                                results.map((course) => {
                                    const courseKey = getCourseKey(course);
                                    const isSelected = courseKey === selectedCourseKey;

                                    return (
                                        <button
                                            key={courseKey}
                                            type="button"
                                            onClick={() => setSelectedCourseKey(courseKey)}
                                            className={`flex w-full items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 ${
                                                isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                                            }`}
                                        >
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-sm font-medium text-gray-900">
                                                        {course.courseInfo.subject} {course.courseInfo.number}
                                                    </span>
                                                    {course.userItemId && (
                                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                                                            Already in My Courses
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-1 truncate text-sm text-gray-600">
                                                    {course.topic
                                                        ? `${course.courseInfo.titleShort} - ${course.topic}`
                                                        : course.courseInfo.titleShort}
                                                </div>
                                            </div>
                                            <div className="pt-0.5 text-xs text-gray-400">
                                                {course.userState.status.replaceAll("_", " ")}
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-6 py-4">
                    <div className="text-sm text-gray-500">
                        {selectedCourse
                            ? `Selected: ${selectedCourse.courseInfo.subject} ${selectedCourse.courseInfo.number}`
                            : "Select a course to continue."}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={!selectedCourse || isSubmitting}
                            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                        >
                            {isSubmitting ? "Adding..." : "Add course"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
